/**
 * Browse a Veeva Vault connection (FS-908). Rendered inside
 * `ConnectionBrowserPage`, which owns the page header and the connection
 * picker; this component is handed the chosen `connectionId` and does nothing
 * with routing.
 *
 * Two tabs:
 *  - "VQL query": the VeevaQueryBuilder reads live vault metadata and assembles
 *    VQL into the editable box (hand-edit before running if you like). Running
 *    executes the VQL via /query and shows a dynamic table whose columns are
 *    whatever the query returned. When the query reads from `documents`, each
 *    row gets a Download action, plus "Download all" (server-side zip) and
 *    "Export CSV" for the whole result set.
 *  - "FTP Browser": the vault's File Staging area (Veeva's "FTP" server) —
 *    navigate folders, download, upload, rename, delete (FS-913).
 *
 * Reads resolve the credential server-side; nothing sensitive is entered here.
 * Document downloads stream the file bytes (audited: document.downloaded /
 * document.bulk_downloaded).
 */

import { useCallback, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconDownload,
  IconFileZip,
  IconFolders,
  IconSearch,
  IconTableExport,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import {
  downloadCsv,
  downloadFile,
  veevaDocumentContentUrl,
  veevaDownloadAll,
  veevaQuery,
} from "@/shared/api/integration-browse";
import { VeevaQueryBuilder } from "./VeevaQueryBuilder";
import { VeevaFileStaging } from "./VeevaFileStaging";

function cell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// Filename for a query row that represents a document. Only trust the row's
// `filename__v` when it carries an extension; otherwise return undefined so
// the caller falls back to the server's Content-Disposition, which has the
// correct name + extension (guessing one from `format__v` produced
// unopenable, extension-less files).
function recordDocName(r: Record<string, unknown>): string | undefined {
  const filename = r.filename__v;
  if (typeof filename === "string" && filename.includes(".")) {
    return filename.replace(/[\\/:*?"<>|]/g, "_");
  }
  return undefined;
}

// A query that reads from the `documents` object → rows are downloadable.
function queryTargetsDocuments(vql: string): boolean {
  return /\bfrom\s+documents\b/i.test(vql);
}

export function VeevaBrowser({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();

  const [vql, setVql] = useState("");
  const [records, setRecords] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    if (!vql.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await veevaQuery(token, connectionId, vql);
      setRecords(result.records);
      setTotal(result.total);
      setRan(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken, connectionId, vql]);

  // Per-row download for a query row that represents a document. `name` may be
  // undefined — downloadFile then uses the server's filename.
  const onDownloadRecord = async (id: string, name?: string) => {
    setDownloading(id);
    try {
      const token = await getToken();
      await downloadFile(token, veevaDocumentContentUrl(connectionId, id), name);
    } catch (err) {
      toastApiError("Download failed", err);
    } finally {
      setDownloading(null);
    }
  };

  // Zip every document matched by the current VQL (server-side, audited).
  const onDownloadAll = async () => {
    setBulkBusy(true);
    try {
      const token = await getToken();
      const res = await veevaDownloadAll(token, connectionId, vql);
      toastSuccess(
        "Download ready",
        res.truncated
          ? `Zipped the first ${res.count} of ${res.matched} documents (capped).`
          : `Zipped ${res.count} document${res.count === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      toastApiError("Download all failed", err);
    } finally {
      setBulkBusy(false);
    }
  };

  const onExportCsv = () => {
    if (records.length === 0) return;
    downloadCsv("veeva-results.csv", records);
  };

  const targetsDocuments = queryTargetsDocuments(vql);

  const columns = useMemo(() => {
    const keys = new Set<string>();
    for (const r of records) for (const k of Object.keys(r)) keys.add(k);
    return Array.from(keys);
  }, [records]);

  return (
    <Stack gap="md">
      <Tabs defaultValue="query" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="query" leftSection={<IconSearch size={16} />}>
            VQL query
          </Tabs.Tab>
          <Tabs.Tab value="ftp" leftSection={<IconFolders size={16} />}>
            FTP Browser
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="query">
          <Stack gap="md">
            <Paper withBorder radius="md" p="md">
              <Stack gap="sm">
                <VeevaQueryBuilder connectionId={connectionId} onVqlChange={setVql} />
                <Textarea
                  label="VQL query"
                  description="Assembled from the builder above — edit by hand before running if you like."
                  value={vql}
                  onChange={(e) => setVql(e.currentTarget.value)}
                  autosize
                  minRows={2}
                />
                <Group justify="flex-end">
                  <Button
                    leftSection={<IconSearch size={16} />}
                    onClick={() => void run()}
                    loading={loading}
                    disabled={!vql.trim()}
                  >
                    Run query
                  </Button>
                </Group>
              </Stack>
            </Paper>

            {error && (
              <Alert variant="light" color="red" title="Query failed">
                {error}
              </Alert>
            )}

            {records.length > 0 && (
              <Group justify="flex-end" gap="xs">
                {targetsDocuments && (
                  <Button
                    variant="default"
                    size="xs"
                    leftSection={<IconFileZip size={16} />}
                    onClick={() => void onDownloadAll()}
                    loading={bulkBusy}
                  >
                    Download all
                  </Button>
                )}
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconTableExport size={16} />}
                  onClick={onExportCsv}
                >
                  Export CSV
                </Button>
              </Group>
            )}

            <Paper withBorder radius="md" p={0}>
              {loading ? (
                <Group justify="center" p="xl">
                  <Loader size="sm" />
                </Group>
              ) : records.length === 0 ? (
                <Text c="dimmed" p="xl" ta="center">
                  {ran ? "No rows." : "Build a query above and press Run."}
                </Text>
              ) : (
                <Stack gap={0}>
                  {total !== null && (
                    <Text size="xs" c="dimmed" p="xs">
                      {total} row{total === 1 ? "" : "s"} total
                    </Text>
                  )}
                  <Table.ScrollContainer minWidth={400}>
                    <Table verticalSpacing="sm" striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          {columns.map((c) => (
                            <Table.Th key={c}>{c}</Table.Th>
                          ))}
                          {targetsDocuments && <Table.Th style={{ width: 60 }}></Table.Th>}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {records.map((r, i) => (
                          <Table.Tr key={i}>
                            {columns.map((c) => (
                              <Table.Td key={c}>{cell(r[c])}</Table.Td>
                            ))}
                            {targetsDocuments && (
                              <Table.Td>
                                {r.id !== undefined && r.id !== null ? (
                                  <Tooltip label="Download">
                                    <ActionIcon
                                      variant="subtle"
                                      color="brand"
                                      loading={downloading === String(r.id)}
                                      onClick={() =>
                                        void onDownloadRecord(String(r.id), recordDocName(r))
                                      }
                                      aria-label={`Download ${recordDocName(r) ?? String(r.id)}`}
                                    >
                                      <IconDownload size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                ) : (
                                  <Text size="sm" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </Table.Td>
                            )}
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="ftp">
          <VeevaFileStaging connectionId={connectionId} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
